// ./src/components/features/Artists/EventArtistsList/EventArtistsList.tsx
//
// Event artist bookings list — table on desktop (>= md), cards below. Tap-safe,
// rights-gated, selection-ready, swipe on phones. Each row shows the act, its
// billing + booking status, and its guarantee / paid. Mirrors EventPartnersList.

import React, { useCallback } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  Avatar,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { STATUSES } from "@sdflc/utils";

import {
  EventArtist,
  eventArtistDisplayName,
  eventArtistPhotoUrl,
  billingLabel,
  bookingStatusLabel,
  bookingStatusColor,
} from "@/utils/api";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  CardSkeletonCheckbox,
  countCardActions,
  type CardSkeletonShape,
} from "@/components/generic";
import { recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";

export interface EventArtistsListProps {
  data: EventArtist[];
  loading?: boolean;
  /**
   * ⚠ REQUIRED — whether the FIRST read has come back. A skeleton belongs to that read only; a
   * refetch keeps the rows OR the empty state on screen and lets ContentLoadingOverlay say it is
   * reloading. `loading && items.length === 0` re-showed it on every refetch of an EMPTY list, so
   * an empty page read skeleton → "none" → skeleton → "none". Required so a caller cannot drop it.
   */
  isLoaded: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEdit?: (a: EventArtist) => void;
  onDelete?: (a: EventArtist) => void;
  onAdd?: () => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  selection?: RowSelection;
  selectable?: boolean;
  hasQueryNarrowing?: boolean;
  /**
   * #165 round 2 — the act's set as one line under its name ("8:00 PM – 9:00 PM · Main stage" on the date picked on the
   * page, else "Plays 3 dates"); null shows nothing. The page knows the date, the event's zone and the map.
   */
  setLineOf?: (a: EventArtist) => string | null;
  /** #165 round 2 — the date the page shows ("Sat, Oct 18"): an empty list then says nobody is on THAT date yet. */
  dateLabel?: string | null;
}

/** The act's set under its name — top-aligned with the text it belongs to (the house rule for icons beside text). */
const SetLine: React.FC<{ line: string | null }> = ({ line }) =>
  line ? (
    <Group gap={6} wrap="nowrap" align="flex-start" c="dimmed">
      <AppIcon iconName="Clock" size={16} style={{ marginTop: 4, flexShrink: 0 }} />
      <Text size="md" c="dimmed" lineClamp={2}>
        {line}
      </Text>
    </Group>
  ) : null;

const isDisabled = (a: EventArtist): boolean => a.status === STATUSES.DISABLED;

const money = (amount?: number | null, currency?: string | null): string | null => {
  if (amount == null) {
    return null;
  }

  const value = amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return currency ? `${currency} ${value}` : value;
};

const StatusBadge: React.FC<{ a: EventArtist }> = ({ a }) => {
  const { t } = useTranslation(["common"]);

  if (!isDisabled(a)) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color="gray">
      {t("common:status.hidden", { defaultValue: "Hidden" })}
    </Badge>
  );
};

const BillingBadge: React.FC<{ a: EventArtist }> = ({ a }) => {
  if (!a.billing) {
    return null;
  }

  return (
    <Badge size="xs" variant="outline" color="grape">
      {billingLabel(a.billing)}
    </Badge>
  );
};

const BookingBadge: React.FC<{ a: EventArtist }> = ({ a }) => {
  if (!a.bookingStatus) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color={bookingStatusColor(a.bookingStatus)}>
      {bookingStatusLabel(a.bookingStatus)}
    </Badge>
  );
};

/** The bill: its tier, and the act's position on it. */
const BillingCell: React.FC<{ a: EventArtist }> = ({ a }) => {
  const { t } = useTranslation(["artists"]);

  if (!a.billing && a.billingIndex == null) {
    return (
      <Text size="md" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Group gap={6} wrap="nowrap">
      <BillingBadge a={a} />
      {a.billingIndex != null && (
        <Text size="md" c="dimmed">
          {t("artists:list.position", { defaultValue: "#{{n}}", n: a.billingIndex })}
        </Text>
      )}
    </Group>
  );
};

/** The touring party's headcount, said as one ("Party of 4") — a bare "1" under a name meant nothing. */
const PartyLine: React.FC<{ a: EventArtist }> = ({ a }) => {
  const { t } = useTranslation(["artists"]);

  return a.entourageCount != null && a.entourageCount > 0 ? (
    <Text size="md" c="dimmed" lineClamp={1}>
      {t("artists:list.party", { defaultValue: "Party of {{count}}", count: a.entourageCount })}
    </Text>
  ) : null;
};

const ArtistAvatar: React.FC<{ a: EventArtist; size?: number }> = ({ a, size = 34 }) => {
  const url = eventArtistPhotoUrl(a);

  return (
    <Avatar src={url || undefined} radius="xl" size={size} color="grape">
      <AppIcon iconName="Microphone" size={Math.round(size * 0.5)} />
    </Avatar>
  );
};

const MoneyCell: React.FC<{ a: EventArtist }> = ({ a }) => {
  const { t } = useTranslation(["artists"]);
  const agreedStr = money(a.amount, a.currencyCode);
  // Paid is derived from the linked event budget item (payments live in the budget now).
  const paid = a.budgetItem?.paidAmount ?? 0;

  if (agreedStr == null && paid === 0) {
    return (
      <Text size="xs" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Box>
      <Text size="md" fw={600}>
        {agreedStr ?? money(0, a.currencyCode)}
      </Text>
      <Text size="xs" c="dimmed">
        {t("artists:list.paid", { defaultValue: "Paid {{paid}}", paid: money(paid, a.currencyCode) ?? "0" })}
      </Text>
    </Box>
  );
};

const RowActions: React.FC<{
  a: EventArtist;
  onEdit?: (r: EventArtist) => void;
  onDelete?: (r: EventArtist) => void;
  isDeleting: boolean;
}> = ({ a, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation(["common"]);

  const showEdit = !!onEdit && recordHasRight(a, "edit");
  const showDelete = !!onDelete && recordHasRight(a, "remove");

  return (
    <Group gap={4} wrap="nowrap">
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon variant="subtle" color="blue" size="md" onClick={() => onEdit(a)} disabled={isDeleting}>
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon variant="subtle" color="red" size="md" onClick={() => onDelete(a)} loading={isDeleting} disabled={isDeleting}>
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

interface RowProps {
  a: EventArtist;
  name: string;
  setLine: string | null;
  disabled: boolean;
  onEdit?: (r: EventArtist) => void;
  onDelete?: (r: EventArtist) => void;
  highlighted: boolean;
  isDeleting: boolean;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

const ArtistTableRow: React.FC<RowProps> = ({
  a, name, setLine, disabled, onEdit, onDelete, highlighted, isDeleting,
  selectionColumn, selectable, selected, onToggleSelect,
}) => (
  <Table.Tr
    style={{
      opacity: isDeleting ? 0.5 : disabled ? 0.6 : 1,
      backgroundColor: highlighted ? HIGHLIGHT_ROW_BG : selected ? "var(--mantine-color-blue-light)" : undefined,
      transition: "background-color 150ms ease, opacity 150ms ease",
    }}
  >
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        {selectable && (
          <Checkbox checked={selected} onChange={() => a.id && onToggleSelect?.(a.id)} disabled={isDeleting} aria-label={name} />
        )}
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <ArtistAvatar a={a} />
        <Box style={{ minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            <StatusBadge a={a} />
          </Group>
          <SetLine line={setLine} />
          <PartyLine a={a} />
        </Box>
      </Group>
    </Table.Td>
    {/* Large screens: the bill and the booking get their own columns (owner, 2026-10-16) — scanned down a lineup,
        not hunted for beside each name. */}
    <Table.Td style={{ width: 170 }}>
      <BillingCell a={a} />
    </Table.Td>
    <Table.Td style={{ width: 150 }}>
      <BookingBadge a={a} />
    </Table.Td>
    <Table.Td style={{ width: 200 }}>
      <MoneyCell a={a} />
    </Table.Td>
    <Table.Td style={{ width: 120 }}>
      <RowActions a={a} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />
    </Table.Td>
  </Table.Tr>
);

const ArtistTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Skeleton height={14} width="40%" maw={200} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 170 }}>
      <Skeleton height={18} width={90} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 150 }}>
      <Skeleton height={18} width={80} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 200 }}>
      <Skeleton height={14} width={120} />
    </Table.Td>
    <Table.Td style={{ width: 120 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const ArtistCardComponent: React.FC<CardProps> = ({
  a, name, setLine, disabled, onEdit, onDelete, highlighted, isDeleting,
  selectable, selected, selectionMode, onToggleSelect, forceClose, onForceCloseComplete, onSwipeStateChange,
}) => {
  const { t } = useTranslation(["common"]);
  const toggleSelect = () => a.id && onToggleSelect?.(a.id);

  const swipeActions = React.useMemo((): SwipeAction[] => {
    const acts: SwipeAction[] = [];

    if (onEdit && recordHasRight(a, "edit")) {
      acts.push({ key: "edit", icon: "Edit", label: t("common:buttons.edit"), color: "blue", onClick: () => onEdit(a), disabled: isDeleting });
    }
    if (onDelete && recordHasRight(a, "remove")) {
      acts.push({ key: "delete", icon: "Trash", label: t("common:buttons.delete"), color: "red", onClick: () => onDelete(a), loading: isDeleting, disabled: isDeleting });
    }

    return acts;
  }, [onEdit, onDelete, a, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : disabled ? 0.6 : 1}
      style={{ backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined, ...(highlighted ? HIGHLIGHT_STYLE : {}) }}
    >
      <Group gap="md" wrap="nowrap" align="center">
        {showCheckbox && (
          <Checkbox checked={selected} onChange={toggleSelect} onClick={(e) => e.stopPropagation()} disabled={isDeleting} aria-label={name} />
        )}
        <ArtistAvatar a={a} size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            <StatusBadge a={a} />
          </Group>
          <SetLine line={setLine} />
          <PartyLine a={a} />
          <Group gap={4} mt={4} wrap="wrap">
            <BillingBadge a={a} />
            <BookingBadge a={a} />
          </Group>
          <Box mt={6}>
            <MoneyCell a={a} />
          </Box>
        </Box>
        {showActions && <RowActions a={a} onEdit={onEdit} onDelete={onDelete} isDeleting={isDeleting} />}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box hiddenFrom="sm" onClick={selectable ? toggleSelect : undefined} style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}>
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const ArtistCard = React.memo(ArtistCardComponent);

const ArtistCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="center">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={4} sizes={['md', 'xs', 'md', 'xs']} gap={2} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

const EventArtistsListComponent: React.FC<EventArtistsListProps> = ({
  data,
  loading = false,
  isLoaded,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  onEdit,
  onDelete,
  onAdd,
  isRemoving = () => false,
  highlightedId,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
  setLineOf,
  dateLabel = null,
}) => {
  const { t } = useTranslation(["artists", "common"]);
  const { isDesktop } = useAppEnvironment();

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onEdit, onDelete);
  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  const handleEdit = useCallback((a: EventArtist) => { closeAllCards(); onEdit?.(a); }, [onEdit, closeAllCards]);
  const handleDelete = useCallback((a: EventArtist) => { closeAllCards(); onDelete?.(a); }, [onDelete, closeAllCards]);
  const handleSwipeStateChange = useCallback((id: string, isOpen: boolean) => { if (isOpen) { openCard(id); } }, [openCard]);

  // ⚠ The FIRST read only — see `isLoaded` on the props. ▶ npm run check-list-states
  const showSkeleton = !isLoaded;

  if (!showSkeleton && data.length === 0) {
    return (
      <Paper withBorder p="xl" radius="md">
        {hasQueryNarrowing ? (
          <EmptyState
            title={t("artists:emptyState.noMatchTitle", { defaultValue: "No bookings match" })}
            description={t("artists:emptyState.noMatchDescription", { defaultValue: "Try a different search or clear the filters." })}
            iconName="Search"
            size="md"
          />
        ) : (
          <EmptyState
            title={
              dateLabel
                ? t("artists:emptyState.dateTitle", { defaultValue: "No acts on {{date}} yet", date: dateLabel })
                : t("artists:emptyState.title", { defaultValue: "No acts booked yet" })
            }
            description={
              dateLabel
                ? t("artists:emptyState.dateDescription", { defaultValue: "Book the acts playing that date, or copy another date's lineup to it." })
                : t("artists:emptyState.description", { defaultValue: "Book the acts on this event's lineup and track their deals and paperwork." })
            }
            iconName="Microphone"
            actionLabel={t("artists:emptyState.actionLabel", { defaultValue: "Book an act" })}
            onAction={onAdd}
            size="md"
          />
        )}
      </Paper>
    );
  }

  const rowIsSelectable = (a: EventArtist): boolean =>
    isSelectable && ((!!onEdit && recordHasRight(a, "edit")) || (!!onDelete && recordHasRight(a, "remove")));

  const rowProps = (a: EventArtist): RowProps => ({
    a,
    name: eventArtistDisplayName(a),
    setLine: setLineOf ? setLineOf(a) : null,
    disabled: isDisabled(a),
    onEdit: onEdit ? handleEdit : undefined,
    onDelete: onDelete ? handleDelete : undefined,
    highlighted: a.id === highlightedId,
    isDeleting: isRemoving(a.id),
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(a),
    selected: selection?.isSelected(a.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = data.filter((r) => rowIsSelectable(r)).map((r) => r.id).filter((id): id is string => !!id);
  const loadMore = <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />;

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                    />
                  </Table.Th>
                )}
                <Table.Th>{t("artists:list.columns.name", { defaultValue: "Act" })}</Table.Th>
                <Table.Th style={{ width: 170 }}>{t("artists:list.columns.billing", { defaultValue: "Billing" })}</Table.Th>
                <Table.Th style={{ width: 150 }}>{t("artists:list.columns.booking", { defaultValue: "Booking" })}</Table.Th>
                <Table.Th style={{ width: 200 }}>{t("artists:list.columns.money", { defaultValue: "Money" })}</Table.Th>
                <Table.Th style={{ width: 120 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 6 }).map((_, i) => <ArtistTableRowSkeleton key={i} selectionColumn={isSelectable} />)
                : data.map((a) => <ArtistTableRow key={a.id} {...rowProps(a)} />)}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 5 }).map((_, i) => <ArtistCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />)}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Stack gap="sm">
        {data.map((a) => (
          <ArtistCard
            key={a.id}
            {...rowProps(a)}
            forceClose={shouldForceClose(a.id || "")}
            onForceCloseComplete={() => handleForceCloseComplete(a.id || "")}
            onSwipeStateChange={(isOpen) => handleSwipeStateChange(a.id || "", isOpen)}
          />
        ))}
      </Stack>
      {loadMore}
    </Stack>
  );
};

export const EventArtistsList = React.memo(EventArtistsListComponent);
